import { formatValue } from "../utils/helpers";
import metricMetadataIcon from '../assets/metric-metadata-icon.svg';
import { useEffect, useRef, useState } from "react";
import MetricMetadataModal from "./MetricMetadataModal";
import { DisaggregatedMetric, DistributionData, GeoDetails, GeoTag, MetricMetadata, RawIndicatorData } from "../services/types";
import { fetchDistributionData, fetchIndicatorBinCount, fetchMetricMetadata } from "../utils/dashboardUtils";
import IconTooltip from "./IconTooltip";

interface MetricCardProps {
    metric: DisaggregatedMetric;
    width: string;
    disaggData: DisaggregatedMetric[] | null;
    geoDetails: GeoDetails[];
    geoTag: GeoTag | null;
    selectionActive: boolean;
    svg?: string;
    showMetadataIcon?: boolean;
};

const MetricCard = ({ metric, disaggData, geoDetails, geoTag, selectionActive, svg, showMetadataIcon = true }: MetricCardProps) => {
    const [showMetadata, setShowMetadata] = useState(false);
    const [metadata, setMetadata] = useState<MetricMetadata[] | null>(null);
    const [distributionData, setDistributionData] = useState<RawIndicatorData[] | null>(null);
    const [countyDistributionData, setCountyDistributionData] = useState<DistributionData[] | null>(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const [edgeProximity, setEdgeProximity] = useState<{ top: boolean, right: boolean, bottom: boolean, left: boolean }>({
        top: false,
        right: false,
        bottom: false,
        left: false
    });

    const tooltipRef = useRef<HTMLDivElement>(null);

    // Destructure metric properties
    const { id, name, versions } = metric;
    const { value, unit, unit_descriptor, percent_calc } = versions[0];

    const val = unit === "ratio" ? `${percent_calc?.n}/${percent_calc?.d}` : value;

    const handleClick = async () => {
        setShowMetadata((prev) => !prev);

        if (metadata && distributionData && countyDistributionData) {
            // Skip fetching if all data is already present
            return;
        }

        setLoading(true);

        const fetchMetadata = metadata ? Promise.resolve({ data: metadata }) : fetchMetricMetadata(id);
        const fetchDistData = distributionData ? Promise.resolve(distributionData) : fetchIndicatorBinCount(id, geoDetails, geoTag);
        const fetchCountyDist = countyDistributionData ? Promise.resolve(countyDistributionData) : fetchDistributionData(id, [], null);

        try {
            const [metadataResponse, distributionDataResponse, countyDistributionDataResponse] = await Promise.all([
                fetchMetadata,
                fetchDistData,
                fetchCountyDist,
            ]);

            if (!metadata) setMetadata(metadataResponse?.data || null);
            if (!distributionData) setDistributionData(distributionDataResponse || null);
            if (!countyDistributionData) setCountyDistributionData(countyDistributionDataResponse || null);

        } catch (error) {
            const errorMessage = (error as Error).message || 'An unexpected error occurred.';
            console.error('Error fetching data:', error);
            setError(new Error(`Failed to load data: ${errorMessage}`)); // Pass detailed error message
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (tooltipRef.current) {
            const tooltipRect = tooltipRef.current.getBoundingClientRect();
            const proximity = {
                top: tooltipRect.top < 50,
                right: window.innerWidth - tooltipRect.right < 200,
                bottom: window.innerHeight - tooltipRect.bottom < 200,
                left: tooltipRect.left < 50
            };
            setEdgeProximity(proximity);
        }
    }, []);

    return (
        <div className="mx-2 my-2">
            <div className="flex flex-col items-start w-full">
                <div className="flex items-center justify-start">
                    { svg && <img src={svg} alt={`${name} icon`} className="w-[60px] h-[60px] text-blue-500" /> }
                    
                    <h2 className="text-lacey-text text-3xl font-barlow-condensed font-bold ml-2">{formatValue(val, unit, id)}</h2>
                </div>
                <div className="flex my-1 max-w-44">
                    <p className={`text-lacey-text text-xs font-bold`}>{name}</p>

                    {showMetadataIcon && 
                        <div ref={tooltipRef} className="shrink-0 -mt-[3px] ml-2 relative group">
                            <button onClick={handleClick}>
                                <img src={metricMetadataIcon} alt="Metric metadata icon"/>
                            </button>
                            <IconTooltip edgeProximity={edgeProximity}/>
                        </div>
                    }
                </div>
                {unit_descriptor && <p className={`text-lacey-text text-xs`}>{unit_descriptor}</p>}
            </div>
            <MetricMetadataModal
                isOpen={showMetadata}
                onClose={handleClick}
                id={id}
                metadata={metadata}
                stateData={null}
                nationalData={null}
                disaggData={disaggData}
                distributionData={distributionData}
                countyDistributionData={countyDistributionData}
                selectionActive={selectionActive}
                svg={svg}
                loading={loading}
                error={error}
            />
        </div>
    );
};


export default MetricCard;
